/**
 * Invoices page — list + inline create/edit modal + settings drawer.
 *
 * One self-contained file so the full module is easy to review. Role-gated
 * at the router level (owner/admin only).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, FileText, Trash2, Download, Send, CheckCircle2, DollarSign,
  Settings as SettingsIcon, X, Loader2, Paintbrush, Upload, Pencil, ExternalLink, Bell, Link as LinkIcon,
  History, Calendar, Clock,
} from "lucide-react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, downloadFile } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import MailtoAttachmentModal from "@/components/MailtoAttachmentModal";
import PaymentPlanModal from "@/components/PaymentPlanModal";
import EntityHistoryDrawer from "@/components/EntityHistoryDrawer";

const STATUS_STYLES = {
  draft:   { bg: "#F59E0B", fg: "#FFFFFF", label: "Draft" },     // amber
  sent:    { bg: "#0b5d9e", fg: "#FFFFFF", label: "Sent" },      // brand blue
  partial: { bg: "#8B5CF6", fg: "#FFFFFF", label: "Partial" },   // violet
  paid:    { bg: "#15803D", fg: "#FFFFFF", label: "Paid" },      // emerald
  overdue: { bg: "#DC2626", fg: "#FFFFFF", label: "Overdue" },   // red
  void:    { bg: "#7C3AED", fg: "#FFFFFF", label: "Void" },      // violet
};
// Softer tints for status pills on each row (so the row doesn't shout)
const STATUS_PILL = {
  draft:   { bg: "#FEF3C7", fg: "#92400E" },
  sent:    { bg: "#DBEAFE", fg: "#1E40AF" },
  partial: { bg: "#EDE9FE", fg: "#5B21B6" },
  paid:    { bg: "#DCFCE7", fg: "#166534" },
  overdue: { bg: "#FEE2E2", fg: "#991B1B" },
  void:    { bg: "#EDE9FE", fg: "#5B21B6" },
};
const BRAND = "#0b5d9e";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {} for new, invoice for edit
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFor, setPaymentFor] = useState(null);     // invoice object for Record-payment modal
  const [paymentsLogFor, setPaymentsLogFor] = useState(null); // invoice for history drawer
  const [auditFor, setAuditFor] = useState(null);             // invoice for the audit-trail drawer
  const [planFor, setPlanFor] = useState(null);               // invoice for payment plan modal
  const [mailtoDraft, setMailtoDraft] = useState(null);   // props for MailtoAttachmentModal

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/invoices", { params: statusFilter ? { status: statusFilter } : {} }),
        api.get("/invoices/summary"),
      ]);
      setInvoices(l.data);
      setSummary(s.data);
    } catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const onSend = async (inv) => {
    if (!inv.bill_to_email) { toast.error("Add a client email first"); return; }
    try {
      const r = await api.post(`/invoices/${inv.id}/send`);
      if (r.data.delivery_mode === "mailto" && r.data.mailto_url) {
        setMailtoDraft({
          title: `Send invoice ${inv.invoice_number}`,
          subtitle: `To ${inv.bill_to_email}`,
          to: r.data.bill_to_email || inv.bill_to_email,
          subject: r.data.email_subject,
          body: r.data.email_body,
          mailtoUrl: r.data.mailto_url,
          pdfDownloadUrl: r.data.pdf_download_url,
          pdfFilename: r.data.pdf_filename,
        });
      } else {
        toast.success(r.data.email_sent ? `Email sent to ${inv.bill_to_email}` : "Marked as sent (email failed — resend later)");
      }
      load();
    } catch { toast.error("Send failed"); }
  };
  const onExport = async (inv) => {
    try { await downloadFile(`/invoices/${inv.id}/export`, `Invoice-${inv.invoice_number}.pdf`); }
    catch { toast.error("Export failed"); }
  };
  const onPayLink = async (id) => {
    try {
      const r = await api.post(`/invoices/${id}/pay-link`);
      await navigator.clipboard?.writeText(r.data.url);
      toast.success("Pay link copied to clipboard"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Pay link failed"); }
  };
  const onRemind = async (inv) => {
    if (!inv.bill_to_email) { toast.error("Add a bill-to email first"); return; }
    try {
      const r = await api.post(`/invoices/${inv.id}/remind`);
      const tierLabel = r.data.tier_label || "Reminder";
      if (r.data.delivery_mode === "mailto" && r.data.mailto_url) {
        setMailtoDraft({
          title: `${tierLabel} — Invoice ${inv.invoice_number}`,
          subtitle: `${r.data.days_overdue > 0 ? r.data.days_overdue + " days overdue · " : ""}Reminder #${r.data.reminder_count}`,
          to: r.data.bill_to_email || inv.bill_to_email,
          subject: r.data.email_subject,
          body: r.data.email_body,
          mailtoUrl: r.data.mailto_url,
          pdfDownloadUrl: r.data.pdf_download_url,
          pdfFilename: r.data.pdf_filename,
        });
      } else if (r.data.delivered) {
        toast.success(`${tierLabel} #${r.data.reminder_count} sent to ${r.data.sent_to}`);
      } else {
        toast.info(`Reminder logged (delivery failed — retry later). Count: ${r.data.reminder_count}`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reminder failed"); }
  };

  // Days-overdue helper so the row can decide whether to surface the chase CTA.
  const daysOverdue = (inv) => {
    if (!inv.due_date) return 0;
    const now = new Date();
    const due = new Date(inv.due_date + "T00:00:00");
    return Math.floor((now - due) / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Services + products billing"
        testid="invoices-header"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSettingsOpen(true)}
              data-testid="invoice-settings-btn"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <SettingsIcon size={14} /> Settings
            </button>
            <button
              onClick={() => setEditing({})}
              data-testid="invoice-new-btn"
              className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 flex items-center gap-1.5"
            >
              <Plus size={14} /> New invoice
            </button>
          </div>
        }
      />
      <PageBody>
        {/* KPI strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="invoice-kpis">
            {["draft", "sent", "partial", "paid", "overdue", "void"].map((k) => {
              const sel = statusFilter === k;
              const style = STATUS_STYLES[k];
              return (
                <button
                  key={k}
                  onClick={() => setStatusFilter(statusFilter === k ? "" : k)}
                  data-testid={`kpi-${k}`}
                  className={`p-3 border text-left transition-all hover:scale-[1.02] ${sel ? "ring-2 ring-offset-2" : ""}`}
                  style={{
                    background: style.bg,
                    color: style.fg,
                    borderColor: style.bg,
                    boxShadow: sel ? `0 0 0 2px ${style.bg}` : undefined,
                  }}
                >
                  <div className="text-[10px] uppercase tracking-widest opacity-80">
                    {style.label}
                  </div>
                  <div className="font-mono-fin text-lg mt-1">{fmtMoney(summary[k].total)}</div>
                  <div className="text-xs opacity-80">{summary[k].count} invoice{summary[k].count === 1 ? "" : "s"}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Table */}
        <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }}>
          {loading ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
              <Loader2 className="mx-auto animate-spin mb-2" size={16} /> Loading…
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center" data-testid="invoices-empty">
              <FileText size={32} className="mx-auto mb-3 opacity-40" />
              <div className="font-display font-bold mb-1">No invoices yet</div>
              <div className="text-sm text-[var(--ll-ink-soft)] mb-4">Create your first invoice to start billing.</div>
              <button
                onClick={() => setEditing({})}
                className="text-sm px-3 py-2 bg-[#0b5d9e] text-white inline-flex items-center gap-1.5"
                data-testid="invoices-empty-new"
              >
                <Plus size={14} /> New invoice
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#0b5d9e] text-white">
                <tr className="text-[10px] uppercase tracking-widest">
                  <th className="px-4 py-2.5 text-left">Number</th>
                  <th className="px-4 py-2.5 text-left">Date</th>
                  <th className="px-4 py-2.5 text-left">Bill To</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                  <th className="px-4 py-2.5 text-left">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const s = STATUS_STYLES[inv.status] || STATUS_STYLES.draft;
                  const pill = STATUS_PILL[inv.status] || STATUS_PILL.draft;
                  return (
                    <tr key={inv.id} className="border-t hover:bg-[var(--ll-bg-card)]" data-testid={`invoice-row-${inv.id}`} style={{ borderColor: "var(--ll-border)" }}>
                      <td className="px-4 py-2.5 font-mono-fin">{inv.invoice_number}</td>
                      <td className="px-4 py-2.5 text-[var(--ll-ink-soft)]">{fmtDate(inv.invoice_date)}</td>
                      <td className="px-4 py-2.5 truncate max-w-[220px]" title={inv.bill_to_name}>{inv.bill_to_name}</td>
                      <td className="px-4 py-2.5 text-right font-mono-fin">
                        {fmtMoney(inv.total)}
                        {inv.status === "partial" && (
                          <div className="text-[10px] text-[#5B21B6] mt-0.5" data-testid={`invoice-partial-${inv.id}`}>
                            Paid {fmtMoney(inv.paid_amount || 0)} · {fmtMoney((inv.total || 0) - (inv.paid_amount || 0))} due
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest" style={{ background: pill.bg, color: pill.fg }}>
                          {s.label}
                        </span>
                        {inv.linked_txn_id && (
                          <span title="Linked to a matching bank deposit" className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-[#0b5d9e]" data-testid={`invoice-linked-${inv.id}`}>
                            <LinkIcon size={10} /> Linked
                          </span>
                        )}
                        {(inv.reminders?.length || 0) > 0 && (
                          <span title={`${inv.reminders.length} reminder(s) sent`} className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-[#B45309]" data-testid={`invoice-reminders-${inv.id}`}>
                            <Bell size={10} /> {inv.reminders.length}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <IconBtn title="Export PDF" onClick={() => onExport(inv)} testid={`invoice-export-${inv.id}`}><Download size={13} /></IconBtn>
                          {inv.status !== "paid" && inv.status !== "void" && (
                            <>
                              <IconBtn title="Send email" onClick={() => onSend(inv)} testid={`invoice-send-${inv.id}`}><Send size={13} /></IconBtn>
                              {daysOverdue(inv) >= 1 && (
                                <IconBtn
                                  title={`Chase (auto-pick tier · ${daysOverdue(inv)} days overdue)`}
                                  onClick={() => onRemind(inv)}
                                  testid={`invoice-remind-${inv.id}`}
                                >
                                  <Bell size={13} />
                                </IconBtn>
                              )}
                              {daysOverdue(inv) >= 1 && (
                                <IconBtn
                                  title="Set up a payment plan"
                                  onClick={() => setPlanFor(inv)}
                                  testid={`invoice-plan-${inv.id}`}
                                >
                                  <Calendar size={13} />
                                </IconBtn>
                              )}
                              <IconBtn title="Create pay link" onClick={() => onPayLink(inv.id)} testid={`invoice-paylink-${inv.id}`}><ExternalLink size={13} /></IconBtn>
                              <IconBtn
                                title={inv.status === "partial" ? "Record another payment" : "Record payment"}
                                onClick={() => setPaymentFor(inv)}
                                testid={`invoice-markpaid-${inv.id}`}
                              >
                                <DollarSign size={13} />
                              </IconBtn>
                            </>
                          )}
                          {(inv.payments?.length || 0) > 0 && (
                            <IconBtn title={`Payment history (${inv.payments.length})`} onClick={() => setPaymentsLogFor(inv)} testid={`invoice-history-${inv.id}`}>
                              <History size={13} />
                            </IconBtn>
                          )}
                          <IconBtn
                            title="View change log (who edited what)"
                            onClick={() => setAuditFor(inv)}
                            testid={`invoice-audit-${inv.id}`}
                          >
                            <Clock size={13} />
                          </IconBtn>
                          <IconBtn title="Edit" onClick={() => setEditing(inv)} testid={`invoice-edit-${inv.id}`}><Pencil size={13} /></IconBtn>
                          <IconBtn title="Delete" onClick={() => onDelete(inv.id)} testid={`invoice-delete-${inv.id}`} danger><Trash2 size={13} /></IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </PageBody>

      {editing !== null && (
        <InvoiceEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {settingsOpen && (
        <InvoiceSettingsDrawer onClose={() => setSettingsOpen(false)} />
      )}
      {paymentFor && (
        <RecordPaymentModal
          invoice={paymentFor}
          onClose={() => setPaymentFor(null)}
          onSaved={(result) => {
            setPaymentFor(null);
            load();
            if (result?.receiptDraft) setMailtoDraft(result.receiptDraft);
          }}
        />
      )}
      {paymentsLogFor && (
        <PaymentHistoryDrawer
          invoice={paymentsLogFor}
          onClose={() => setPaymentsLogFor(null)}
        />
      )}
      {planFor && (
        <PaymentPlanModal
          invoice={planFor}
          onClose={() => setPlanFor(null)}
          onCreated={(result) => {
            setPlanFor(null);
            load();
            // If backend returned a mailto draft for the agreement, pop the attachment modal
            const ag = result?.agreement;
            if (ag?.mailto_url) {
              setMailtoDraft({
                title: `Payment plan for Invoice ${planFor.invoice_number}`,
                subtitle: `To ${ag.bill_to_email || planFor.bill_to_email}`,
                to: ag.bill_to_email || planFor.bill_to_email,
                subject: ag.email_subject,
                body: ag.email_body,
                mailtoUrl: ag.mailto_url,
                // No PDF for plan agreement — just email body
              });
            } else if (ag?.email_sent) {
              toast.success(`Agreement emailed to ${ag.bill_to_email}`);
            }
          }}
        />
      )}
      <MailtoAttachmentModal
        open={!!mailtoDraft}
        onClose={() => setMailtoDraft(null)}
        {...(mailtoDraft || {})}
      />
      <EntityHistoryDrawer
        open={!!auditFor}
        onClose={() => setAuditFor(null)}
        entityType="invoice"
        entityId={auditFor?.id}
        entityLabel={auditFor ? `${auditFor.invoice_number} · ${auditFor.bill_to_name} · ${fmtMoney(auditFor.total)}` : null}
      />
    </>
  );
}


function IconBtn({ children, title, onClick, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1.5 border hover:bg-[var(--ll-mint)] ${danger ? "hover:bg-red-50 hover:text-red-600" : ""}`}
      style={{ borderColor: "var(--ll-border)" }}
    >
      {children}
    </button>
  );
}


/* ---------------- Editor modal ---------------- */

function emptyLine() {
  return { kind: "service", description: "", quantity: 1, unit_price: 0 };
}

function InvoiceEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState(() => ({
    invoice_date: initial?.invoice_date || new Date().toISOString().slice(0, 10),
    due_date: initial?.due_date || "",
    bill_to_name: initial?.bill_to_name || "",
    bill_to_email: initial?.bill_to_email || "",
    bill_to_address: initial?.bill_to_address || "",
    lines: initial?.lines?.length ? initial.lines : [emptyLine()],
    discount_percent: initial?.discount_percent || 0,
    tax_percent: initial?.tax_percent ?? 0,
    notes: initial?.notes || "",
    terms: initial?.terms || "",
    invoice_number: initial?.invoice_number || "",
  }));
  const [saving, setSaving] = useState(false);

  // Pull default tax/terms from settings on NEW only
  useEffect(() => {
    if (!isNew) return;
    (async () => {
      try {
        const r = await api.get("/invoice-settings");
        setForm((f) => ({
          ...f,
          tax_percent: r.data.tax_percent ?? 0,
          terms: f.terms || r.data.terms || "",
          notes: f.notes || r.data.notes || "",
        }));
      } catch { /* ignore */ }
    })();
  }, [isNew]);

  const setLine = (i, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, k) => (k === i ? { ...l, ...patch } : l)) }));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, k) => k !== i) }));

  const totals = useMemo(() => {
    const subtotal = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const discount = subtotal * (Number(form.discount_percent) || 0) / 100;
    const taxable = subtotal - discount;
    const tax = taxable * (Number(form.tax_percent) || 0) / 100;
    return { subtotal, discount, tax, total: taxable + tax };
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bill_to_name.trim()) { toast.error("Add a bill-to name"); return; }
    if (form.lines.length === 0 || !form.lines.some((l) => l.description.trim())) {
      toast.error("Add at least one line item with a description"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines: form.lines.filter((l) => l.description.trim()).map((l) => ({
          kind: l.kind,
          description: l.description,
          sku: l.sku || null,
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
        })),
        discount_percent: Number(form.discount_percent) || 0,
        tax_percent: Number(form.tax_percent) || 0,
      };
      if (!payload.due_date) delete payload.due_date;
      if (!payload.invoice_number) delete payload.invoice_number;
      if (isNew) await api.post("/invoices", payload);
      else       await api.patch(`/invoices/${initial.id}`, payload);
      toast.success(isNew ? "Invoice created" : "Invoice updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex justify-end" onClick={onClose} data-testid="invoice-editor">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white h-full w-full max-w-3xl shadow-xl overflow-y-auto"
      >
        <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">{isNew ? "Create" : "Edit"}</div>
            <div className="font-display font-bold text-sm">{isNew ? "New invoice" : `Invoice ${initial.invoice_number}`}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="invoice-editor-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Meta row */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Invoice date">
              <input type="date" required value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="input" data-testid="invoice-date-input" />
            </Field>
            <Field label="Due date (optional)">
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="input" data-testid="invoice-due-input" />
            </Field>
            <Field label="Number (auto if blank)">
              <input type="text" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="input font-mono-fin" placeholder="INV-00042" data-testid="invoice-number-input" />
            </Field>
          </div>

          {/* Bill-to */}
          <div className="border p-4" style={{ borderColor: "var(--ll-border)" }}>
            <div className="text-xs font-display font-bold mb-2">Bill to</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name *">
                <input required value={form.bill_to_name} onChange={(e) => setForm({ ...form, bill_to_name: e.target.value })} className="input" data-testid="invoice-billto-name" />
              </Field>
              <Field label="Email">
                <input type="email" value={form.bill_to_email} onChange={(e) => setForm({ ...form, bill_to_email: e.target.value })} className="input" data-testid="invoice-billto-email" />
              </Field>
              <Field label="Address" full>
                <textarea rows={2} value={form.bill_to_address} onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })} className="input" data-testid="invoice-billto-address" />
              </Field>
            </div>
          </div>

          {/* Lines */}
          <div className="border" style={{ borderColor: "var(--ll-border)" }}>
            <div className="px-3 py-2 bg-[#0b5d9e] text-white text-[10px] uppercase tracking-widest flex items-center justify-between">
              <span>Line items</span>
              <button type="button" onClick={addLine} className="flex items-center gap-1 hover:underline" data-testid="invoice-add-line">
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="p-3 space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-line-${i}`}>
                  <select value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value })} className="input col-span-2" data-testid={`invoice-line-kind-${i}`}>
                    <option value="service">Service</option>
                    <option value="product">Product</option>
                  </select>
                  <input placeholder="Description" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="input col-span-4" data-testid={`invoice-line-desc-${i}`} />
                  {l.kind === "product" ? (
                    <input placeholder="SKU" value={l.sku || ""} onChange={(e) => setLine(i, { sku: e.target.value })} className="input col-span-2 font-mono-fin text-xs" data-testid={`invoice-line-sku-${i}`} />
                  ) : (
                    <div className="col-span-2" />
                  )}
                  <input type="number" step="0.01" min="0" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="input col-span-1 text-right font-mono-fin" data-testid={`invoice-line-qty-${i}`} />
                  <input type="number" step="0.01" min="0" placeholder="Rate" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className="input col-span-2 text-right font-mono-fin" data-testid={`invoice-line-rate-${i}`} />
                  <button type="button" onClick={() => rmLine(i)} className="col-span-1 p-2 border hover:bg-red-50 hover:text-red-600 justify-self-end" style={{ borderColor: "var(--ll-border)" }} data-testid={`invoice-line-remove-${i}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-3">
              <Field label="Discount %">
                <input type="number" step="0.01" min="0" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="input" data-testid="invoice-discount-pct" />
              </Field>
              <Field label="Tax %">
                <input type="number" step="0.01" min="0" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className="input" data-testid="invoice-tax-pct" />
              </Field>
            </div>
            <div className="border p-4" style={{ borderColor: "var(--ll-border)" }}>
              <TotalRow label="Subtotal" value={totals.subtotal} />
              {totals.discount > 0 && <TotalRow label={`Discount (${form.discount_percent}%)`} value={-totals.discount} />}
              {totals.tax > 0 && <TotalRow label={`Tax (${form.tax_percent}%)`} value={totals.tax} />}
              <div className="h-px my-2" style={{ background: "var(--ll-border)" }} />
              <TotalRow label="Total" value={totals.total} bold />
            </div>
          </div>

          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="invoice-notes" />
          </Field>
          <Field label="Terms">
            <textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="input" data-testid="invoice-terms" />
          </Field>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5" data-testid="invoice-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {isNew ? "Create invoice" : "Save changes"}
          </button>
        </div>
      </form>

      <style>{`
        .input { width: 100%; border: 1px solid var(--ll-border); padding: 6px 8px; font-size: 13px; background: white; }
        .input:focus { outline: 2px solid ${BRAND}; outline-offset: -1px; }
      `}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">{label}</div>
      {children}
    </label>
  );
}

function TotalRow({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between py-1 ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-[var(--ll-ink-soft)]">{label}</span>
      <span className="font-mono-fin">{fmtMoney(value)}</span>
    </div>
  );
}


/* ---------------- Settings drawer ---------------- */

function InvoiceSettingsDrawer({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    (async () => {
      const r = await api.get("/invoice-settings");
      setSettings(r.data);
    })();
  }, []);

  if (!settings) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center">
        <Loader2 className="animate-spin text-white" size={24} />
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/invoice-settings", settings);
      setSettings(r.data);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setSettings((s) => ({ ...s, logo_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const matchLogoColor = async () => {
    if (!settings.logo_base64) { toast.error("Upload a logo first"); return; }
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = settings.logo_base64;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      const canvas = document.createElement("canvas");
      canvas.width = 80; canvas.height = 80;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 80, 80);
      const { data } = ctx.getImageData(0, 0, 80, 80);
      // Pick dominant non-near-white, non-near-black pixel (bucket into 32-step cube)
      const buckets = {};
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (a < 200) continue;
        // Skip near-white and near-black
        if (r > 240 && g > 240 && b > 240) continue;
        if (r < 15 && g < 15 && b < 15) continue;
        const key = `${r >> 5}-${g >> 5}-${b >> 5}`;
        buckets[key] = (buckets[key] || { r: 0, g: 0, b: 0, c: 0 });
        buckets[key].r += r; buckets[key].g += g; buckets[key].b += b; buckets[key].c += 1;
      }
      let best = null;
      for (const k of Object.keys(buckets)) {
        const bucket = buckets[k];
        if (!best || bucket.c > best.c) best = bucket;
      }
      if (!best) { toast.error("Couldn't find a usable color — pick manually"); return; }
      const r = Math.round(best.r / best.c), g = Math.round(best.g / best.c), bl = Math.round(best.b / best.c);
      const hex = "#" + [r, g, bl].map((x) => x.toString(16).padStart(2, "0")).join("");
      setSettings((s) => ({ ...s, theme_color: hex }));
      toast.success(`Matched logo color: ${hex}`);
    } catch { toast.error("Color extraction failed"); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex justify-end" onClick={onClose} data-testid="invoice-settings-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white h-full w-full max-w-lg shadow-xl overflow-y-auto">
        <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">Preferences</div>
            <div className="font-display font-bold text-sm">Invoice settings</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="invoice-settings-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          <section>
            <h3 className="text-xs uppercase tracking-widest font-display font-bold mb-2">Numbering</h3>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Prefix">
                <input value={settings.prefix} onChange={(e) => setSettings({ ...settings, prefix: e.target.value })} className="input" data-testid="settings-prefix" />
              </Field>
              <Field label="Zero-pad">
                <input type="number" min="1" max="10" value={settings.pad} onChange={(e) => setSettings({ ...settings, pad: Number(e.target.value) })} className="input" data-testid="settings-pad" />
              </Field>
              <Field label="Next number">
                <input type="number" min="1" value={settings.next_number} onChange={(e) => setSettings({ ...settings, next_number: Number(e.target.value) })} className="input font-mono-fin" data-testid="settings-next-number" />
              </Field>
            </div>
            <div className="text-xs text-[var(--ll-ink-soft)] mt-1">Next invoice will be <span className="font-mono-fin">{settings.prefix}{String(settings.next_number).padStart(settings.pad, "0")}</span></div>
          </section>

          <section>
            <h3 className="text-xs uppercase tracking-widest font-display font-bold mb-2">Defaults</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Default tax %">
                <input type="number" step="0.01" min="0" max="100" value={settings.tax_percent} onChange={(e) => setSettings({ ...settings, tax_percent: Number(e.target.value) })} className="input" data-testid="settings-tax" />
              </Field>
              <Field label="Net due (days)">
                <input type="number" min="0" max="365" value={settings.due_days} onChange={(e) => setSettings({ ...settings, due_days: Number(e.target.value) })} className="input" data-testid="settings-due-days" />
              </Field>
            </div>
            <Field label="Default terms">
              <textarea rows={2} value={settings.terms || ""} onChange={(e) => setSettings({ ...settings, terms: e.target.value })} className="input" data-testid="settings-terms" />
            </Field>
            <Field label="Default notes">
              <textarea rows={2} value={settings.notes || ""} onChange={(e) => setSettings({ ...settings, notes: e.target.value })} className="input" data-testid="settings-notes" />
            </Field>
          </section>

          <section>
            <h3 className="text-xs uppercase tracking-widest font-display font-bold mb-2">Branding</h3>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 border grid place-items-center bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                {settings.logo_base64
                  ? <img ref={imgRef} src={settings.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" data-testid="settings-logo-preview" />
                  : <FileText size={24} className="opacity-40" />}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onFile} className="hidden" />
                <button type="button" onClick={() => fileRef.current?.click()} className="text-xs px-2 py-1.5 border inline-flex items-center gap-1.5 self-start" style={{ borderColor: "var(--ll-border)" }} data-testid="settings-logo-upload">
                  <Upload size={12} /> Upload logo
                </button>
                {settings.logo_base64 && (
                  <button type="button" onClick={() => setSettings({ ...settings, logo_base64: null })} className="text-xs text-red-600 hover:underline self-start" data-testid="settings-logo-clear">Remove logo</button>
                )}
              </div>
            </div>

            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Theme color</div>
              <div className="flex items-center gap-2 flex-wrap">
                <input type="color" value={settings.theme_color || "#0b5d9e"} onChange={(e) => setSettings({ ...settings, theme_color: e.target.value })} className="h-9 w-16 cursor-pointer border" data-testid="settings-color" />
                <input type="text" value={settings.theme_color || ""} onChange={(e) => setSettings({ ...settings, theme_color: e.target.value })} className="input font-mono-fin w-28" placeholder="#0b5d9e" data-testid="settings-color-hex" />
                <button type="button" onClick={matchLogoColor} disabled={!settings.logo_base64} className="text-xs px-2 py-1.5 border inline-flex items-center gap-1.5 hover:bg-[var(--ll-mint)] disabled:opacity-40" style={{ borderColor: "var(--ll-border)" }} data-testid="settings-match-logo">
                  <Paintbrush size={12} /> Match my logo
                </button>
              </div>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }}>Close</button>
          <button type="button" onClick={save} disabled={saving} className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5" data-testid="invoice-settings-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Record Payment modal (partial-pay-aware) ---------------- */

function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const alreadyPaid = Number(invoice.paid_amount || 0);
  const balanceDue = Math.max(0, Number(invoice.total || 0) - alreadyPaid);
  const [form, setForm] = useState({
    paid_amount: balanceDue.toFixed(2),
    paid_method: "card",
    paid_date: new Date().toISOString().slice(0, 10),
    note: "",
    send_receipt: !!invoice.bill_to_email,
  });
  const [saving, setSaving] = useState(false);

  const amount = Number(form.paid_amount) || 0;
  const isPartial = amount > 0 && amount < balanceDue - 0.009;
  const invalid = amount <= 0 || amount - balanceDue > 0.009;

  const submit = async (e) => {
    e.preventDefault();
    if (invalid) {
      toast.error(amount <= 0 ? "Enter a payment amount" : `Payment of ${fmtMoney(amount)} exceeds balance ${fmtMoney(balanceDue)}`);
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/invoices/${invoice.id}/mark-paid`, {
        paid_amount: amount,
        paid_method: form.paid_method,
        paid_date: form.paid_date,
        note: form.note || null,
        send_receipt: form.send_receipt,
      });
      toast.success(isPartial ? `Partial payment ${fmtMoney(amount)} recorded` : `Invoice ${invoice.invoice_number} fully paid`);
      const receipt = r.data?.receipt;
      if (receipt?.mode === "mailto" && receipt?.mailto_url) {
        onSaved({ receiptDraft: {
          title: `Payment receipt — Invoice ${invoice.invoice_number}`,
          subtitle: `${isPartial ? "Partial payment" : "Paid in full"} · ${fmtMoney(amount)}`,
          to: receipt.bill_to_email || invoice.bill_to_email,
          subject: receipt.email_subject,
          body: receipt.email_body,
          mailtoUrl: receipt.mailto_url,
          pdfDownloadUrl: receipt.pdf_download_url,
          pdfFilename: receipt.pdf_filename,
        }});
      } else {
        if (receipt?.delivered) toast.success(`Receipt emailed to ${invoice.bill_to_email}`);
        onSaved();
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const quickFill = (frac) => {
    setForm((f) => ({ ...f, paid_amount: (balanceDue * frac).toFixed(2) }));
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="record-payment-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border w-full max-w-md shadow-xl"
        style={{ borderColor: "var(--ll-ink)" }}
      >
        <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">Record payment</div>
            <div className="font-display font-bold text-sm">Invoice {invoice.invoice_number} · {invoice.bill_to_name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="record-payment-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Invoice total" value={fmtMoney(invoice.total)} />
            <Stat label="Already paid" value={fmtMoney(alreadyPaid)} tone={alreadyPaid > 0 ? "info" : undefined} />
            <Stat label="Balance due" value={fmtMoney(balanceDue)} tone="warn" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Amount</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ll-ink-soft)]">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.paid_amount}
                  onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                  className="input pl-7 font-mono-fin"
                  data-testid="record-payment-amount"
                  autoFocus
                />
              </div>
              <button type="button" onClick={() => quickFill(0.5)} className="text-[11px] px-2 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }} data-testid="record-payment-half">50%</button>
              <button type="button" onClick={() => quickFill(1)} className="text-[11px] px-2 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }} data-testid="record-payment-full">Full</button>
            </div>
            {isPartial && (
              <div className="text-[11px] mt-1 text-[#5B21B6]" data-testid="record-payment-partial-hint">
                Partial payment — invoice stays open with {fmtMoney(balanceDue - amount)} outstanding
              </div>
            )}
            {amount > balanceDue + 0.009 && (
              <div className="text-[11px] mt-1 text-red-600" data-testid="record-payment-over">
                Exceeds balance due. Reduce the amount to {fmtMoney(balanceDue)} or less.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Method</label>
              <select
                value={form.paid_method}
                onChange={(e) => setForm({ ...form, paid_method: e.target.value })}
                className="input"
                data-testid="record-payment-method"
              >
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="wire">Wire</option>
                <option value="stripe">Stripe</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Date</label>
              <input
                type="date"
                value={form.paid_date}
                onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                className="input"
                data-testid="record-payment-date"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Note (optional)</label>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Deposit — check #4821"
              className="input"
              data-testid="record-payment-note"
              maxLength={200}
            />
          </div>

          {invoice.bill_to_email && (
            <label className="flex items-start gap-2 py-1 cursor-pointer" data-testid="record-payment-receipt-row">
              <input
                type="checkbox"
                checked={form.send_receipt}
                onChange={(e) => setForm({ ...form, send_receipt: e.target.checked })}
                data-testid="record-payment-send-receipt"
                className="mt-1"
              />
              <div className="text-xs">
                <div className="font-medium">Email a receipt to <span className="font-mono-fin">{invoice.bill_to_email}</span></div>
                <div className="text-[11px] text-[var(--ll-ink-soft)]">
                  Includes payment amount, running total, and remaining balance.
                  Honors your Mail-routing preference (System / My mail client / Off).
                </div>
              </div>
            </label>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
          <button type="button" onClick={onClose} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }} data-testid="record-payment-cancel">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || invalid}
            data-testid="record-payment-submit"
            className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Record {fmtMoney(amount || 0)}
          </button>
        </div>

        <style>{`
          .input { width: 100%; border: 1px solid var(--ll-border); padding: 6px 10px; font-size: 13px; background: white; }
          .input:focus { outline: 2px solid #0b5d9e; outline-offset: -1px; }
        `}</style>
      </form>
    </div>
  );
}


function Stat({ label, value, tone }) {
  const bg = tone === "warn" ? "#FEF3C7" : tone === "info" ? "#DBEAFE" : "#F3F4F6";
  const fg = tone === "warn" ? "#92400E" : tone === "info" ? "#1E40AF" : "#1F2937";
  return (
    <div className="border p-2" style={{ background: bg, borderColor: "transparent" }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: fg, opacity: 0.75 }}>{label}</div>
      <div className="font-mono-fin text-sm font-semibold" style={{ color: fg }}>{value}</div>
    </div>
  );
}


/* ---------------- Payment history drawer ---------------- */

function PaymentHistoryDrawer({ invoice, onClose }) {
  const payments = invoice.payments || [];
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const downloadReceipt = async (p) => {
    try {
      await downloadFile(
        `/invoices/${invoice.id}/payments/${p.id}/receipt-pdf`,
        `Receipt-${invoice.invoice_number}-${(p.id || "").slice(0,8)}.pdf`,
      );
    } catch { toast.error("Receipt download failed"); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex justify-end" onClick={onClose} data-testid="payment-history-drawer">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white h-full w-full max-w-md shadow-xl overflow-y-auto"
      >
        <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">Payment history</div>
            <div className="font-display font-bold text-sm">Invoice {invoice.invoice_number}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="payment-history-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Invoice" value={fmtMoney(invoice.total)} />
            <Stat label="Paid" value={fmtMoney(totalPaid)} tone="info" />
            <Stat label="Balance" value={fmtMoney((invoice.total || 0) - totalPaid)} tone="warn" />
          </div>
          {payments.length === 0 ? (
            <div className="text-center text-[var(--ll-ink-soft)] py-6">No payments recorded yet.</div>
          ) : (
            <ol className="space-y-2">
              {payments.slice().sort((a, b) => (b.recorded_at || "").localeCompare(a.recorded_at || "")).map((p, i) => (
                <li key={p.id || i} className="border p-3" style={{ borderColor: "var(--ll-border)" }} data-testid={`payment-row-${p.id || i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono-fin font-bold">{fmtMoney(p.amount)}</div>
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]">{p.method}</div>
                      <button
                        type="button"
                        onClick={() => downloadReceipt(p)}
                        data-testid={`payment-receipt-${p.id}`}
                        title="Download the PDF receipt for this payment"
                        className="text-[10px] uppercase tracking-widest px-2 py-1 border hover:bg-[var(--ll-mint)] flex items-center gap-1"
                        style={{ borderColor: "var(--ll-border)" }}
                      >
                        <Download size={10} /> Receipt
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-[var(--ll-ink-soft)] mt-1">
                    {fmtDate(p.paid_date)}
                    {p.linked_txn_id && (
                      <span title="Linked to bank deposit" className="ml-2 inline-flex items-center gap-0.5 text-[#0b5d9e]">
                        <LinkIcon size={10} /> Linked
                      </span>
                    )}
                  </div>
                  {p.note && <div className="text-xs mt-1 italic text-[var(--ll-ink)]">"{p.note}"</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
